import { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Send,
  Loader2,
  Mic,
  Square,
  Paperclip,
  Zap,
  PenLine,
  Sparkles,
  LayoutList
} from 'lucide-react';

const THINKING_MODES = [
  { id: 'general', label: 'General', icon: Zap, description: 'All-purpose assistant', color: 'text-blue-400' },
  { id: 'emails', label: 'Emails', icon: PenLine, description: 'Draft and reply', color: 'text-amber-400' },
  { id: 'ideas', label: 'Ideas', icon: Sparkles, description: 'Brainstorm and create', color: 'text-violet-400' },
  { id: 'projects', label: 'Projects', icon: LayoutList, description: 'Plan and organize', color: 'text-emerald-400' }
];

const MessageInput = ({
  input,
  setInput,
  mode,
  setMode,
  showModeSelector,
  setShowModeSelector,
  loading,
  streaming,
  isRecording,
  isTranscribing,
  recordingDuration,
  uploadingAttachment,
  currentConversationId,
  memorySuggestions,
  sendMessage,
  handleKeyDown,
  startRecording,
  stopRecording,
  uploadChatAttachment,
  saveMemorySuggestion,
  dismissMemorySuggestion
}) => {
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  
  const currentMode = THINKING_MODES.find(m => m.id === mode) || THINKING_MODES[0];

  // Format recording duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="sticky bottom-0 bg-background border-t border-border px-4 sm:px-6 py-4">
      <div className="max-w-3xl mx-auto">
        {/* Mode Selector */}
        <div className="flex items-center gap-2 mb-3" data-mode-selector>
          <span className="text-xs text-muted-foreground">Mode:</span>
          <div className="relative">
            <button
              onClick={() => setShowModeSelector(!showModeSelector)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs ${currentMode.color} bg-muted hover:bg-muted/80 transition-colors`}
              data-testid="mode-selector-btn"
            >
              <currentMode.icon className="h-3.5 w-3.5" />
              {currentMode.label}
            </button>

            <AnimatePresence>
              {showModeSelector && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  className="absolute bottom-full left-0 mb-2 bg-card border border-border rounded-lg shadow-lg p-2 min-w-[180px] z-50"
                >
                  {THINKING_MODES.map((m) => {
                    const Icon = m.icon;
                    return (
                      <button
                        key={m.id}
                        onClick={() => { setMode(m.id); setShowModeSelector(false); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-md transition-colors ${
                          mode === m.id ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
                        }`}
                        data-testid={`mode-${m.id}`}
                      >
                        <Icon className={`h-4 w-4 ${m.color}`} />
                        <div className="text-left">
                          <div className="text-sm font-medium">{m.label}</div>
                          <div className="text-xs text-muted-foreground">{m.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Memory Suggestions */}
        {memorySuggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-3 p-3 bg-violet-500/10 border border-violet-500/20 rounded-lg"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-violet-400">Save to memory?</span>
            </div>
            {memorySuggestions.map((suggestion, idx) => (
              <div key={idx} className="flex items-center justify-between gap-2 py-1">
                <span className="text-sm text-muted-foreground">{suggestion.content}</span>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => saveMemorySuggestion(suggestion)}
                    className="h-6 px-2 text-xs"
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => dismissMemorySuggestion(suggestion)}
                    className="h-6 px-2 text-xs text-muted-foreground"
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {/* Voice Recording Indicator */}
        <AnimatePresence>
          {isRecording && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="mb-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                  <div className="absolute inset-0 w-2.5 h-2.5 bg-red-500 rounded-full animate-ping opacity-75" />
                </div>
                <div>
                  <p className="text-sm font-medium text-red-400">Recording...</p>
                  <p className="text-xs text-muted-foreground">
                    {recordingDuration !== undefined ? formatDuration(recordingDuration) : 'Speak now'}
                  </p>
                </div>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={stopRecording}
                className="gap-1.5 h-7 text-xs"
              >
                <Square className="h-3 w-3 fill-current" />
                Stop
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Transcribing Indicator */}
        <AnimatePresence>
          {isTranscribing && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="mb-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-center gap-3"
            >
              <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
              <div>
                <p className="text-sm font-medium text-blue-400">Transcribing audio...</p>
                <p className="text-xs text-muted-foreground">Converting speech to text</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Input Area - Compact design */}
        <div className="relative flex items-end gap-2 bg-muted/50 rounded-xl border border-border p-2">
          {/* Left side icons */}
          <div className="flex items-center gap-1 pb-1">
            {/* Attachment button */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt,.md,.csv"
              onChange={(e) => e.target.files?.[0] && uploadChatAttachment(e.target.files[0])}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingAttachment || !currentConversationId}
              className="p-1.5 rounded-full hover:bg-muted transition-colors disabled:opacity-50"
              title="Attach file"
              data-testid="attach-btn"
            >
              {uploadingAttachment ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <Paperclip className="h-4 w-4 text-muted-foreground" />
              )}
            </button>

            {/* Voice button */}
            <button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isTranscribing}
              className={`p-1.5 rounded-full transition-colors ${
                isRecording 
                  ? 'bg-red-500/20 text-red-500' 
                  : 'hover:bg-muted text-muted-foreground'
              }`}
              title={isRecording ? 'Stop recording' : 'Voice input'}
              data-testid="voice-btn"
            >
              {isTranscribing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isRecording ? (
                <Square className="h-3.5 w-3.5 fill-current" />
              ) : (
                <Mic className="h-4 w-4" />
              )}
            </button>
          </div>

          {/* Text Input */}
          <div className="flex-1 min-w-0">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Message Arlo (${currentMode.label} mode)...`}
              className="min-h-[60px] max-h-[200px] resize-none border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-base py-3 px-1"
              disabled={loading || streaming || isTranscribing}
              rows={2}
              data-testid="message-input"
            />
          </div>

          {/* Send button */}
          <div className="pb-1">
            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading || streaming}
              className="p-2 rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="send-btn"
            >
              {loading || streaming ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
        
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};

export default MessageInput;
